<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import { useAlert } from 'dashboard/composables';
import PropertyOffersAPI from 'dashboard/api/propertyOffers';
import { useSalesReceipt } from '../composables/useSalesReceipt';
import SalesDetailFactList from './SalesDetailFactList.vue';
import SalesDetailDocusignTimeline from './SalesDetailDocusignTimeline.vue';
import SalesDetailReceiptDocument from './SalesDetailReceiptDocument.vue';
import SalesReceiptEnvelopeActions from './SalesReceiptEnvelopeActions.vue';

const props = defineProps({
  transaction: { type: Object, required: true },
});

const { t } = useI18n();
const resendingSignerReference = ref('');
const transactionId = computed(() => props.transaction.id);
const propertyOfferId = computed(() => props.transaction.property_offer_id);
const canManageEnvelope = computed(() =>
  Boolean(
    propertyOfferId.value && props.transaction.permissions?.can_save_draft
  )
);
const receipt = computed(() => props.transaction.receipt || null);
const state = useSalesReceipt({
  id: transactionId,
  t,
  notify: useAlert,
  receipt,
});

const receiptItems = computed(() => [
  {
    key: 'type',
    label: t('SALES.DETAIL.RECEIPT.TYPE'),
    value: state.data.value?.signature_type,
  },
  {
    key: 'date',
    label: t('SALES.DETAIL.RECEIPT.SIGNATURE_DATE'),
    value: state.data.value?.signature_date,
  },
]);

const messageFrom = (error, fallback) =>
  error?.response?.data?.errors?.[0] ||
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  fallback;

const resendSigner = async signer => {
  const reference = signer?.signer_reference;
  if (!canManageEnvelope.value || !reference) return;

  resendingSignerReference.value = reference;
  try {
    await PropertyOffersAPI.resendContractSigner(
      propertyOfferId.value,
      reference
    );
    useAlert(
      t('SALES.DETAIL.RECEIPT.RESEND_SUCCESS', { name: signer.display_name })
    );
    await state.load();
  } catch (error) {
    useAlert(messageFrom(error, t('SALES.DETAIL.RECEIPT.ERROR_RESEND')));
  } finally {
    resendingSignerReference.value = '';
  }
};
</script>

<template>
  <div class="grid gap-4">
    <LaisCard
      :title="t('SALES.DETAIL.RECEIPT.TITLE')"
      prefix-icon="i-lucide-file-signature"
    >
      <SalesDetailFactList :items="receiptItems" />
    </LaisCard>

    <LaisCard
      :title="t('SALES.DETAIL.RECEIPT.FILE_TITLE')"
      prefix-icon="i-lucide-paperclip"
    >
      <SalesDetailReceiptDocument
        :document="state.document.value"
        :can-remove="state.canRemove.value"
        :can-upload="state.canUpload.value"
        :removing="state.removing.value"
        :uploading="state.uploading.value"
        @download="state.download"
        @remove="state.remove"
        @upload="state.upload"
      />
    </LaisCard>

    <LaisCard
      v-if="state.data.value?.integration"
      :title="t('SALES.DETAIL.RECEIPT.DOCUSIGN_TITLE')"
      prefix-icon="i-lucide-file-signature"
    >
      <p
        v-if="state.data.value.integration.status === 'not_configured'"
        class="text-sm text-amber-800"
      >
        {{ t('SALES.DETAIL.RECEIPT.DOCUSIGN_NOT_CONFIGURED') }}
      </p>
      <SalesDetailDocusignTimeline
        v-else
        :integration="state.data.value.integration"
        :loading="state.loading.value"
        :can-resend="canManageEnvelope"
        :resending-signer-reference="resendingSignerReference"
        @refresh="state.load"
        @resend-signer="resendSigner"
      />
      <SalesReceiptEnvelopeActions
        v-if="state.data.value.integration.status !== 'not_configured'"
        class="mt-4"
        :integration="state.data.value.integration"
        :property-offer-id="transaction.property_offer_id"
        :can-manage="canManageEnvelope"
        @updated="state.load"
      />
    </LaisCard>
  </div>
</template>
